<template>
  <div class="auth-page">
    <div class="auth-container">
      <div class="auth-header">
        <h1 class="auth-title">로그인</h1>
        <p class="auth-subtitle">분양쟁이에 오신 것을 환영합니다</p>
      </div>

      <form @submit.prevent="handleEmailLogin" class="auth-form">
        <div class="input-group">
          <label for="email">이메일</label>
          <input 
            type="email" 
            id="email" 
            v-model="email" 
            placeholder="이메일을 입력하세요"
            required
          />
        </div>

        <div class="input-group">
          <label for="password">비밀번호</label>
          <input 
            type="password" 
            id="password" 
            v-model="password" 
            placeholder="비밀번호를 입력하세요"
            required
          />
        </div>

        <div class="forgot-password">
          <router-link to="/forgot-password">비밀번호를 잊으셨나요?</router-link>
        </div>

        <p v-if="error" class="error-message">{{ error }}</p>

        <button type="submit" class="btn-primary" :disabled="isLoading">
          {{ isLoading ? '로그인 중...' : '로그인' }}
        </button>
      </form>

      <div class="divider">
        <span>또는</span>
      </div>

      <button @click="handleGoogleLogin" class="btn-google" :disabled="isLoading">
        <svg width="20" height="20" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Google로 계속하기
      </button>

      <p class="auth-link">
        계정이 없으신가요? 
        <router-link to="/register">회원가입</router-link>
      </p>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'

export default {
  name: 'LoginView',
  setup() {
    const router = useRouter()
    const { loginWithEmail, loginWithGoogle, error } = useAuth()
    
    const email = ref('')
    const password = ref('')
    const isLoading = ref(false)

    const handleEmailLogin = async () => {
      isLoading.value = true
      try {
        await loginWithEmail(email.value, password.value)
        router.push('/')
      } catch (e) {
        // 에러는 useAuth에서 처리
      } finally {
        isLoading.value = false
      }
    }

    const handleGoogleLogin = async () => {
      isLoading.value = true
      try {
        await loginWithGoogle()
        router.push('/')
      } catch (e) {
        // 에러는 useAuth에서 처리
      } finally {
        isLoading.value = false
      }
    }

    return {
      email,
      password,
      error,
      isLoading,
      handleEmailLogin,
      handleGoogleLogin
    }
  }
}
</script>

<style scoped>
.auth-page {
  min-height: calc(100vh - 120px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.auth-container {
  width: 100%;
  max-width: 400px;
  background: #fff;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
}

.auth-header {
  text-align: center;
  margin-bottom: 32px;
}

.auth-title {
  font-size: 28px;
  font-weight: 700;
  color: #191f28;
  margin-bottom: 8px;
}

.auth-subtitle {
  font-size: 15px;
  color: #6b7684;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-group label {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.input-group input {
  padding: 14px 16px;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 15px;
  font-family: inherit;
  transition: all 0.15s;
}

.input-group input:focus {
  outline: none;
  border-color: #3182f6;
  box-shadow: 0 0 0 3px rgba(49, 130, 246, 0.1);
}

.input-group input::placeholder {
  color: #adb5bd;
}

.forgot-password {
  text-align: right;
  margin-top: -12px;
}

.forgot-password a {
  font-size: 13px;
  color: #6b7684;
}

.forgot-password a:hover {
  color: #3182f6;
}

.error-message {
  color: #e03131;
  font-size: 14px;
  text-align: center;
  margin: 0;
}

.btn-primary {
  padding: 16px;
  background: #3182f6;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-primary:hover:not(:disabled) {
  background: #1b64da;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.divider {
  display: flex;
  align-items: center;
  margin: 24px 0;
}

.divider::before,
.divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: #e5e8eb;
}

.divider span {
  padding: 0 16px;
  font-size: 13px;
  color: #8b95a1;
}

.btn-google {
  width: 100%;
  padding: 14px;
  background: #fff;
  border: 1px solid #e5e8eb;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  font-family: inherit;
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  transition: all 0.15s;
}

.btn-google:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
}

.btn-google:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.auth-link {
  text-align: center;
  margin-top: 24px;
  font-size: 14px;
  color: #6b7684;
}

.auth-link a {
  color: #3182f6;
  font-weight: 600;
}

.auth-link a:hover {
  text-decoration: underline;
}

@media (max-width: 480px) {
  .auth-container {
    padding: 32px 24px;
  }

  .auth-title {
    font-size: 24px;
  }
}
</style>